<template>
  <div class="mt-2">
    <div class="mb-6 bg-gray-200 p-4 rounded-lg">
      <div class="flex flex-wrap justify-between items-center gap-4">
        <div class="flex items-center flex-grow">
          <label class="mr-2 font-semibold whitespace-nowrap">Store:</label>
          <div class="relative flex-grow max-w-2xl">
            <select
              v-model="selectedStore"
              class="w-full appearance-none bg-white border border-gray-300 rounded-lg py-2 pl-4 pr-8 leading-tight focus:outline-none focus:border-blue-500"
            >
              <option value="all">All Stores</option>
              <option v-for="(name, id) in stores" :key="id" :value="id">
                {{ name }}
              </option>
            </select>
            <div
              class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700"
            >
              <unicon name="angle-down" class="w-4 h-4 fill-current" />
            </div>
          </div>
        </div>

        <div class="flex items-center gap-4">
          <div class="flex items-center">
            <label class="mr-2 font-semibold whitespace-nowrap"
              >Time Period:</label
            >
            <div class="relative">
              <select
                v-model="selectedPeriod"
                class="appearance-none bg-white border border-gray-300 rounded-lg py-2 pl-4 pr-8 leading-tight focus:outline-none focus:border-blue-500"
              >
                <option value="week">Week</option>
                <option value="period">Period</option>
                <option value="quarter">Quarter</option>
                <option value="year">Year</option>
              </select>
              <div
                class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700"
              >
                <unicon name="angle-down" class="w-4 h-4 fill-current" />
              </div>
            </div>
          </div>

          <div class="flex items-center">
            <button
              @click="previousPeriod"
              class="px-2 py-1 rounded bg-gray-300 hover:bg-gray-400"
            >
              <unicon name="angle-left" class="w-4 h-4" fill="currentColor" />
            </button>
            <span class="mx-4 font-semibold">{{ periodLabel }}</span>
            <button
              @click="nextPeriod"
              class="px-2 py-1 rounded bg-gray-300 hover:bg-gray-400"
            >
              <unicon name="angle-right" class="w-4 h-4" fill="currentColor" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
      <div
        v-for="(metric, index) in activeMetrics"
        :key="index"
        class="h-80 w-full"
      >
        <chart
          v-if="getChartData(metric)"
          :ref="`chart-${index}`"
          class="max-w-full h-full"
          :chart-data="getChartData(metric)"
          :options="getOptions(metric.title)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { Pie, mixins } from 'vue-chartjs'
import { mapGetters } from 'vuex'
import { format } from '@/utils.js'
import { DateTime } from 'luxon'

const { reactiveProp } = mixins

export const PieChart = {
  extends: Pie,
  mixins: [reactiveProp],
  props: ['options'],
  mounted() {
    this.renderChart(this.chartData, this.options)
  }
}

export default {
  name: 'StoreMetricsChart',
  components: {
    Chart: PieChart
  },
  data() {
    return {
      selectedStore: 'all',
      selectedPeriod: 'week',
      currentDate: this.$store.getters['time/getDateTime'](),
      metrics: [
        {
          title: 'Sales Channel',
          type: 'channels',
          colors: [
            'rgba(54, 162, 235, 0.8)', // In-store
            'rgba(255, 99, 132, 0.8)', // Online
            'rgba(75, 192, 192, 0.8)', // 3PD
            'rgba(255, 206, 86, 0.8)' // Catering
          ]
        },
        {
          title: 'Net Sales Distribution',
          type: 'distribution',
          colors: [
            'rgba(255, 99, 132, 0.8)', // COGS
            'rgba(54, 162, 235, 0.8)', // Labor
            'rgba(255, 206, 86, 0.8)', // Rent
            'rgba(75, 192, 192, 0.8)', // Merchant Fees
            'rgba(153, 102, 255, 0.8)', // Direct Opex
            'rgba(255, 159, 64, 0.8)', // G&A
            'rgba(201, 203, 207, 0.8)' // RL EBITDA
          ]
        }
      ]
    }
  },
  computed: {
    ...mapGetters('global', ['stores']),
    ...mapGetters('storeModule/reports', ['reportsOrderedReverse']),

    periodLabel() {
      switch (this.selectedPeriod) {
        case 'week':
          return this.currentDate.toFormat('MMM d, yyyy') + ' week'
        case 'period':
          return this.currentDate.toFormat('MMM yyyy') + ' period'
        case 'quarter':
          return (
            'Q' +
            Math.ceil(this.currentDate.month / 3) +
            ' ' +
            this.currentDate.year
          )
        case 'year':
          return this.currentDate.year
        default:
          return ''
      }
    },

    activeMetrics() {
      return this.metrics
    }
  },
  watch: {
    selectedStore() {
      this.refreshData()
    }
  },
  methods: {
    previousPeriod() {
      switch (this.selectedPeriod) {
        case 'week':
          this.currentDate = this.currentDate.minus({ weeks: 1 })
          break
        case 'period':
          this.currentDate = this.currentDate.minus({ months: 1 })
          break
        case 'quarter':
          this.currentDate = this.currentDate.minus({ months: 3 })
          break
        case 'year':
          this.currentDate = this.currentDate.minus({ years: 1 })
          break
      }
      this.refreshData()
    },

    nextPeriod() {
      switch (this.selectedPeriod) {
        case 'week':
          this.currentDate = this.currentDate.plus({ weeks: 1 })
          break
        case 'period':
          this.currentDate = this.currentDate.plus({ months: 1 })
          break
        case 'quarter':
          this.currentDate = this.currentDate.plus({ months: 3 })
          break
        case 'year':
          this.currentDate = this.currentDate.plus({ years: 1 })
          break
      }
      this.refreshData()
    },

    refreshData() {
      // Implement data refresh based on new time period
      // This should probably dispatch a Vuex action to fetch new data
    },

    getChartData(metric) {
      switch (metric.type) {
        case 'channels':
          return {
            labels: ['In-store', 'Online', '3PD', 'Catering'],
            datasets: [
              {
                data: this.getChannelData(),
                backgroundColor: metric.colors,
                borderWidth: 0
              }
            ]
          }
        case 'distribution':
          return {
            labels: [
              'COGS',
              'Labor',
              'Rent',
              'Merchant Fees',
              'Direct Opex',
              'G&A',
              'RL EBITDA'
            ],
            datasets: [
              {
                data: this.getDistributionData(),
                backgroundColor: metric.colors,
                borderWidth: 0
              }
            ]
          }
        default:
          return null
      }
    },

    getChannelData() {
      // TODO: Implement actual data fetching from your store
      return [45000, 25000, 20000, 10000] // Example data
    },

    getDistributionData() {
      // TODO: Implement actual data fetching from your store
      const totalSales = 100000 // Example total net sales
      return [
        30000, // COGS (30%)
        25000, // Labor (25%)
        10000, // Rent (10%)
        5000, // Merchant Fees (5%)
        8000, // Direct Opex (8%)
        7000, // G&A (7%)
        15000 // RL EBITDA (remaining 15%)
      ]
    },

    getOptions(title) {
      return {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: {
            top: 20,
            bottom: 20
          }
        },
        legend: {
          display: true,
          position: 'bottom',
          labels: {
            fontFamily: 'Nunito',
            fontStyle: 'bold',
            fontSize: 12,
            fontColor: '#2C3648',
            padding: 15,
            boxWidth: 12
          }
        },
        title: {
          display: true,
          text: title,
          fontSize: 16,
          fontFamily: 'Nunito',
          fontColor: '#2C3648',
          padding: 10
        },
        aspectRatio: 1,
        cutoutPercentage: 0,
        tooltips: {
          enabled: true,
          mode: 'single',
          callbacks: {
            label: function (tooltipItem, data) {
              const dataset = data.datasets[tooltipItem.datasetIndex]
              const total = dataset.data.reduce((acc, curr) => acc + curr, 0)
              const value = dataset.data[tooltipItem.index]
              const percentage = ((value / total) * 100).toFixed(1)
              return `${data.labels[tooltipItem.index]}: ${format(
                value
              )} (${percentage}%)`
            }
          }
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
/* Add any custom styles if needed */
</style>
